import React, { useContext, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { NotificationsContext } from "../../context/notificationsContext";
import { INotification, INotificationsContext, NotificationType } from "../../types/notifications";
import dayjs from "dayjs";
import { notificationsService } from "../../api/notificationsService/notificationsService";
import Spinner from "../ui/loaders/spinner/Spinner";
import { useNavigate } from "react-router-dom";
import { routeUrls } from "../../config/routeUrls";
import { toast } from "react-toastify";
import Button from "../ui/button/Button";
const PAGINATION_COUNT = 10;

interface ISidebarProps {
    isOpen?: boolean;
    callbackCloseSidebar: () => void;
}

const SidebarNotifications: React.FC<ISidebarProps> = ({
    isOpen,
    callbackCloseSidebar
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const sidebarRef = useRef<HTMLDivElement | null>(null);

    const {
        notificationsToRead,
        setNotificationsToRead
    } = useContext(NotificationsContext) as INotificationsContext;

    const [showedTab, setShowedTab] = useState<"notificationsToRead" | "bulkNotifications">("notificationsToRead");
    const showedTabRef = React.useRef(showedTab);
    const setShowedTabRef = (data: "notificationsToRead" | "bulkNotifications") => {
        showedTabRef.current = data;
        setShowedTab(data);
    };

    // Bulk notifications
    const [bulkNotifications, setBulkNotifications] = useState<INotification[]>([]);
    const bulkNotificationsRef = React.useRef(bulkNotifications);
    const setBulkNotificationsRef = (data: INotification[]) => {
        bulkNotificationsRef.current = data;
        setBulkNotifications(data);
    };

    const [notificationsToReadLocal, setNotificationsToReadLocal] = useState<INotification[]>([]);
    useEffect(() => {
        setNotificationsToReadLocal(notificationsToRead);        
    }, [notificationsToRead]);

    useEffect(() => {
        const refreshInterval = setInterval(async () => {
            try {
                const response = await notificationsService.getNotificationsToRead();
                if (response.success) {
                    setNotificationsToRead(response.responseBody.notifications);
                }
            } catch (error) {
                console.error("Error refreshing notifications", error);
            }
        }, 60 * 1000); // 1 minute

        return () => clearInterval(refreshInterval);
    }, [setNotificationsToRead]);

    useEffect(() => {
        const listener = () => {
            if (!!sidebarRef.current) {
                const scrollTop = sidebarRef.current?.scrollTop;
                const offsetHeight = sidebarRef.current?.offsetHeight;
                const scrollHeight = sidebarRef.current?.scrollHeight - 1;

                if (scrollTop + offsetHeight >= scrollHeight) {
                    getBulkNotifications();
                }
            }
        }

        if (!!sidebarRef.current) {
            if (sidebarRef && sidebarRef.current !== null) {
                sidebarRef.current.addEventListener('scroll', listener);
            }
        }

        return () => {
            if (!!sidebarRef.current) {
                sidebarRef.current.removeEventListener('scroll', listener)
            }
        }
    }, []);

    useEffect(() => {
        if (showedTab === "bulkNotifications") {
            getBulkNotifications(true);
        } else {
            setBulkNotificationsRef([]);
        }
    }, [showedTab]);

    const [readLoading, setReadLoading] = useState<boolean>(false);
    const read = async (id: string) => {
        setReadLoading(true);
        const response = await notificationsService.readNotification(id);
        if (response.success) {
            // Refresh notifications to read
            const notificationsToReadRes = await notificationsService.getNotificationsToRead();
            if (notificationsToReadRes.success) {
                setNotificationsToRead(notificationsToReadRes.responseBody.notifications);
            } else {
                window.location.reload();
            }
        } else {
            toast.error(t('errors.errorReadNotification'));
        }

        setReadLoading(false);

        return {
            success: response.success
        };
    }

    const redirectToNotificationAction = (type: NotificationType) => {
        switch (type) {
            case "arcese_documentation":
                navigate(routeUrls.documents)
                break;
            case "arcese_fleet":
                navigate(routeUrls.fleetAndRoute)
                break;
            case "arcese_route":
                navigate(routeUrls.fleetAndRoute + '?tab=routes')
                break;
            case "mht_contracts":
                navigate(routeUrls.contracts)
                break;
            case "product":
                navigate(routeUrls.services)
                break;
        }

        callbackCloseSidebar();
    }

    const handleNotificationAction = async (type: NotificationType, id: string, isRead: boolean) => {
        if (!isRead) {
            const readNotification = await read(id);

            if (readNotification.success) {
                redirectToNotificationAction(type);
            }
        } else {
            redirectToNotificationAction(type);
        }
    }

    const [loading, setLoading] = useState(false);
    const loadingRef = React.useRef(loading);
    const setloadingRef = (data: boolean) => {
        loadingRef.current = data;
        setLoading(data);
    };

    const [currentPage, setCurrentPage] = useState(1);
    const currentPageRef = React.useRef(currentPage);
    const setCurrentPageRef = (data: number) => {
        currentPageRef.current = data;
        setCurrentPage(data);
    };

    const [lengthLastRequest, setLengthLastRequest] = useState<number | null>(null);
    const lengthLastRequestRef = React.useRef(lengthLastRequest);
    const setLengthLastRequestRef = (data: null | number) => {
        lengthLastRequestRef.current = data;
        setLengthLastRequest(data);
    };

    const canGoNext = () => {
        return (lengthLastRequestRef.current === null || (lengthLastRequestRef.current !== null && !(lengthLastRequestRef.current < PAGINATION_COUNT)))
    }

    const getBulkNotifications = async (refresh: boolean = false) => {
        if (showedTabRef.current === "bulkNotifications" && !loadingRef.current && (canGoNext() || refresh === true)) {
            setloadingRef(true);
            const response = await notificationsService.getNotificationsBulk(refresh ? 1 : currentPageRef.current, PAGINATION_COUNT);
            if (response.success) {
                const notifics = response.responseBody.notifications;
                setLengthLastRequestRef(notifics.length);
                if (!refresh) {
                    setBulkNotificationsRef([...bulkNotificationsRef.current.concat(notifics)]);
                    setCurrentPageRef(currentPageRef.current + 1);
                } else {
                    setBulkNotificationsRef(notifics);
                    setCurrentPageRef(2);
                }
            } else {
                console.log('Error getting notifications');
                toast.error(t('errors.errorGetNotifications'))
            }

            setloadingRef(false);
        }
    }

    const getNotificationText = (notification: INotification) => {
        const baseResult = <div onClick={() => handleNotificationAction(notification.type, notification.id, notification.isRead)} className="text-[15px] leading-[18px] cursor-pointer hover:underline">
            {notification.description}
        </div>;

        const localDescription = notification.description;
        if (notification && !!localDescription) {
            const indexStart = localDescription.indexOf('#[') !== -1 ? (localDescription.indexOf('#[')) : -1;
            const indexEnd = localDescription.indexOf(']#') !== -1 ? (localDescription.indexOf(']#') + 2) : -1;

            if (indexStart !== -1 && indexEnd !== -1) {
                const string = localDescription.slice(indexStart + 2, indexEnd - 2);
                const firstPart = localDescription.substring(0, indexStart);
                const lastPart = localDescription.substring(indexEnd, localDescription.length);

                return (
                    <div>
                        {firstPart}<span onClick={() => handleNotificationAction(notification.type, notification.id, notification.isRead)} className="text-[15px] leading-[18px] cursor-pointer underline">{string}</span>{lastPart}
                    </div>
                )
            } else {
                return baseResult;
            }
        }

        return baseResult;
    }

    const handleBulkRead = async () => {
        setReadLoading(true);
        const response = await notificationsService.readAllNotifications();
        if (response.success) {
            // Refresh notifications to read
            const notificationsToReadRes = await notificationsService.getNotificationsToRead();
            if (notificationsToReadRes.success) {
                setNotificationsToRead(notificationsToReadRes.responseBody.notifications);
            } else {
                window.location.reload();
            }
        } else {
            toast.error(t('errors.errorReadNotification'));
        }

        setReadLoading(false);
    };

    return (
        <aside
            className={"flex flex-col transition z-10 fixed right-0 top-0 h-[100dvh] w-screen md:w-[520px] bg-white overflow-auto hide-scrollbar" + (isOpen === true ? " translate-x-0 sidebar-shadow" : " translate-x-[100%]")}
        >
            <div className="shrink-0 h-[88px] px-[30px] pt-[32px] pb-[24px] border-b flex justify-between items-center">
                <div className="text-lg font-bold leading-[21.6px] flex items-center">
                    <span>{t('common.sidebars.notifications.heading')}</span>
                    {
                        readLoading &&
                        <div className="ml-2">
                            <Spinner />
                        </div>
                    }
                    <div className="relative ml-4">
                        <button 
                            onClick={handleBulkRead}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                            </svg>
                            {notificationsToReadLocal.length > 0 && (
                                <span className="absolute top-0 -right-1 bg-red text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                                    {notificationsToReadLocal.length}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
                <div onClick={callbackCloseSidebar} className="cursor-pointer">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </div>
            </div>

            {/* MAIN CONTENT OF SIDEBAR */}
            <div ref={sidebarRef} className="grow overflow-y-auto overflow-x-hidden px-[30px] py-8">
                {
                    showedTab === "bulkNotifications" &&
                    <>
                        <div className="cursor-pointer uppercase font-bold text-sm mb-6 flex items-center" onClick={() => setShowedTabRef("notificationsToRead")}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 stroke-red">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                            </svg>

                            <div className="ml-2">{t('common.sidebars.addServices.goBack')}</div>
                        </div>

                        {
                            bulkNotifications.length > 0 &&
                            <div>
                                {
                                    bulkNotifications.map((notification, index) => {
                                        return (
                                            <div key={index} className="pb-4 border-b mb-6">
                                                <div className="flex items-center justify-between">
                                                    <div className="mb-4 flex items-center pr-4">                                                        
                                                        {
                                                            getNotificationText(notification)
                                                        }

                                                    </div>                                                   
                                                </div>

                                                <div className="text-[13px] font-semibold text-[#6F6F6F]">{dayjs(notification.date).format('DD/MM/YYYY')}</div>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        }

                        {
                            bulkNotifications.length === 0 && !loading &&
                            <div>
                                {t('common.sidebars.notifications.emptyNotifications')}
                            </div>
                        }

                        <>
                            {
                                loadingRef.current &&
                                <div className="flex justify-center items-center">
                                    <Spinner height={40} width={40} />
                                </div>
                            }
                        </>
                    </>
                }

                {
                    showedTab === "notificationsToRead" &&
                    <>
                        {
                            notificationsToReadLocal.length > 0 &&
                            <div>
                                {
                                    notificationsToReadLocal.map((notification, index) => {
                                        return (
                                            <div key={index} className="pb-4 border-b mb-6">
                                                <div className="flex items-center justify-between">
                                                    <div className="mb-4 flex items-center pr-4">
                                                        {
                                                            notification.isRead === false &&
                                                            <div className="mr-4 h-[10px] w-[10px] bg-red rounded-full shrink-0"></div>
                                                        }

                                                        {
                                                            getNotificationText(notification)
                                                        }

                                                    </div>

                                                    {
                                                        notification.isRead === false &&
                                                        <div
                                                            className="cursor-pointer"
                                                            onClick={() => read(notification.id)}
                                                        >
                                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M12 18H6L2 22V4C2 2.9 2.9 2 4 2H20C21.1 2 22 2.9 22 4V11H20V4H4V16H12V18ZM23 14.34L21.59 12.93L17.35 17.17L15.23 15.05L13.82 16.46L17.34 20L23 14.34Z" fill="#CF0A2C" />
                                                            </svg>
                                                        </div>
                                                    }
                                                </div>

                                                <div className="text-[13px] font-semibold text-[#6F6F6F]">{dayjs(notification.date).format('DD/MM/YYYY')}</div>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        }

                        {
                            notificationsToReadLocal.length === 0 &&
                            <div>
                                {t('common.sidebars.notifications.emptyNotificationsToRead')}
                            </div>
                        }

                        <div>
                            <Button className="mt-6" callback={() => setShowedTabRef("bulkNotifications")}>
                                {t('common.sidebars.notifications.seeAllNotifications')}
                            </Button>
                        </div>
                    </>
                }
            </div>
        </aside>
    )
}

export default React.memo(SidebarNotifications);